import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: '1. Información que recopilamos',
    text: 'Recopilamos únicamente la información necesaria para procesar tu compra: nombre, correo electrónico y datos de pago. No almacenamos información de tarjetas de crédito ni datos bancarios directamente — estos son procesados de forma segura por nuestra plataforma de pago.'
  }, {
    heading: '2. Uso de la información',
    text: 'Tu información se utiliza exclusivamente para: enviar el producto digital adquirido, comunicarnos contigo sobre tu compra, y enviarte información relevante solo si has dado tu consentimiento.'
  }, {
    heading: '3. Protección de datos',
    text: 'Implementamos medidas de seguridad para proteger tu información personal. No vendemos, intercambiamos ni transferimos tu información personal a terceros sin tu consentimiento.'
  }, {
    heading: '4. Cookies',
    text: 'Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación y analizar el tráfico del sitio. Puedes configurar tu navegador para rechazar cookies si lo prefieres.'
  }, {
    heading: '5. Tus derechos',
    text: 'Tienes derecho a acceder, rectificar o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, puedes comunicarte con nosotros a través de los canales disponibles.'
  }, {
    heading: '6. Cambios en esta política',
    text: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Los cambios serán publicados en esta misma página.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: '1. Producto digital',
    text: 'Al realizar tu compra, adquieres acceso a un producto digital en formato PDF descargable. Este producto es para uso personal y no puede ser redistribuido, revendido ni compartido sin autorización.'
  }, {
    heading: '2. Entrega del producto',
    text: 'Una vez confirmado el pago, recibirás un correo electrónico con el enlace de descarga. La entrega es inmediata y automática. Si no recibes el correo, revisa tu carpeta de spam.'
  }, {
    heading: '3. Garantía de satisfacción',
    text: 'Ofrecemos una garantía de 7 días a partir de la fecha de compra. Si no estás satisfecha con el producto, puedes solicitar un reembolso completo dentro de ese período sin necesidad de justificación.'
  }, {
    heading: '4. Propiedad intelectual',
    text: 'Todo el contenido del producto — textos, imágenes, diseño y estructura — está protegido por derechos de autor. Queda prohibida su reproducción total o parcial sin autorización expresa.'
  }, {
    heading: '5. Limitación de responsabilidad',
    text: 'Este producto tiene fines informativos y educativos. No sustituye el consejo médico profesional. Consulta a un profesional de la salud antes de realizar cambios significativos en tu alimentación o estilo de vida.'
  }, {
    heading: '6. Modificaciones',
    text: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor desde su publicación en el sitio.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const content = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="bg-olive text-white py-12 px-4 text-center" data-id="element-96">
        <div className="max-w-4xl mx-auto flex flex-col items-center justify-center space-y-4 opacity-90" data-id="element-97">
          <p className="font-medium" data-id="element-98">
            © {new Date().getFullYear()} Plan Antiinflamatorio +40 · Todos los
            derechos reservados
          </p>
          <p className="text-sm text-white/70" data-id="element-99">Pago 100% seguro</p>

          <div className="flex items-center gap-4 text-sm text-white/60" data-id="element-100">
            <button onClick={() => setActiveModal('privacy')} className="hover:text-white transition-colors underline underline-offset-2" data-id="element-101">
              Política de Privacidad
            </button>
            <span className="text-white/30" data-id="element-102">·</span>
            <button onClick={() => setActiveModal('terms')} className="hover:text-white transition-colors underline underline-offset-2" data-id="element-103">
              Términos y Condiciones
            </button>
          </div>

          <p className="text-xs text-white/50 mt-8 max-w-2xl" data-id="element-104">
            Este sitio no es parte del sitio web de Facebook o Facebook Inc.
            Además, este sitio no está respaldado por Facebook de ninguna
            manera. FACEBOOK es una marca registrada de FACEBOOK, Inc. Los
            resultados pueden variar de persona a persona.
          </p>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-105">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-106">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-107">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 flex-shrink-0" data-id="element-108">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-olive" data-id="element-109">
                  {content.title}
                </h2>
                <button onClick={() => setActiveModal(null)} className="p-2 rounded-full hover:bg-cream transition-colors text-warm-gray hover:text-olive" data-id="element-110">
                  <X className="w-5 h-5" data-id="element-111" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto px-6 py-6 space-y-6 flex-1" data-id="element-112">
                <p className="text-sm text-warm-gray" data-id="element-113">
                  Última actualización:{' '}
                  {new Date().toLocaleDateString('es-MX', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
                </p>
                {content.sections.map((section, idx) => <div key={idx} data-id="element-114">
                    <h3 className="font-bold text-olive mb-2 font-heading text-lg" data-id="element-115">
                      {section.heading}
                    </h3>
                    <p className="text-warm-gray leading-relaxed" data-id="element-116">
                      {section.text}
                    </p>
                  </div>)}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0" data-id="element-117">
                <button onClick={() => setActiveModal(null)} className="w-full bg-sage hover:bg-sage-dark text-white font-bold py-3 px-6 rounded-full transition-colors text-sm" data-id="element-118">
                  Entendido
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}